/**
 * @voltxampere/engine — Capacitor transient companion stamp
 *
 * SPECS §3.8 capacitor companion. The DC stamp was a no-op (open
 * circuit at DC); this transient stamp lays a Norton equivalent —
 * `Geq` between the two terminals plus a `±Ieq` current source that
 * carries the previous-step contribution.
 *
 *   I_C(t) = Geq · V_C(t) + Ieq      (companion form)
 *
 * The dispatcher (`integration/dispatcher.ts`) picks the right
 * companion for the active method. Sign convention matches the
 * diode stamp: the Norton current sits with `-Ieq` at the positive
 * terminal RHS and `+Ieq` at the negative terminal RHS, which keeps
 * the global KCL "current INTO node" convention intact.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { selectCapacitorCompanion } from "../../transient/integration/dispatcher.js";
import { defineTransientStamp } from "./types.js";
import { getTerminalNetId, getNumericParam } from "../terminal-utils.js";
/** Read a node's previous voltage with the ground-compression `-1` convention. */
function nodeVoltage(x, idx) {
    if (idx < 0)
        return 0;
    return x[idx] ?? 0;
}
export const capacitorTransientStamp = (ctx, component) => {
    const C = getNumericParam(component, "capacitance");
    const posIdx = ctx.nodeIndex(getTerminalNetId(component, "p1"));
    const negIdx = ctx.nodeIndex(getTerminalNetId(component, "p2"));
    // V_C(t-h) and V_C(t-2h) come from the previous *step's* converged
    // solution, not the current Newton iterate (`ctx.xPrev`). The
    // companion is constant across Newton iters within a single time
    // step — only the diode-style stamps re-linearize per iter.
    const VCPrev = nodeVoltage(ctx.xStepPrev, posIdx) - nodeVoltage(ctx.xStepPrev, negIdx);
    const ICPrev = ctx.previousCurrent(component.id);
    const VCPrevPrev = ctx.xStepPrevPrev === null
        ? null
        : nodeVoltage(ctx.xStepPrevPrev, posIdx) - nodeVoltage(ctx.xStepPrevPrev, negIdx);
    const { Geq, Ieq } = selectCapacitorCompanion({
        method: ctx.method,
        C,
        h: ctx.h,
        hPrev: ctx.hPrev,
        VCPrev,
        ICPrev,
        VCPrevPrev,
    });
    // Conductance stamp — symmetric four-corner Norton pattern.
    ctx.addY(posIdx, posIdx, Geq);
    ctx.addY(negIdx, negIdx, Geq);
    ctx.addY(posIdx, negIdx, -Geq);
    ctx.addY(negIdx, posIdx, -Geq);
    // Norton current source — same RHS sign discipline as the diode.
    if (posIdx >= 0)
        ctx.rhs[posIdx] = (ctx.rhs[posIdx] ?? 0) - Ieq;
    if (negIdx >= 0)
        ctx.rhs[negIdx] = (ctx.rhs[negIdx] ?? 0) + Ieq;
};
/** Capacitor transient stamp keeps the DC `[]` augmentation — Norton, no extra row. */
export const capacitorTransientStampDefinition = defineTransientStamp("capacitor", capacitorTransientStamp, []);
//# sourceMappingURL=capacitor.js.map